import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Drop, Calendar, Check, X } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryPage() {
  const { t, language } = useLanguage();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/washes/history`, { withCredentials: true });
        setHistory(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'sq' ? 'sq-AL' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] safe-bottom flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0ABAB5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6">
        <h1 className="text-2xl font-medium tracking-tight font-['Outfit']">{t('washHistory')}</h1>
        <p className="text-white/50 mt-1">Your past washes</p>
      </div>

      {/* History List */}
      <div className="px-4 pb-8">
        {history.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
              <Drop size={40} weight="duotone" className="text-white/20" />
            </div>
            <p className="text-white/50">{t('noHistory')}</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {history.map((wash, index) => (
              <motion.div
                key={wash.wash_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 rounded-2xl bg-[#121212] border border-white/5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0ABAB5]/10 flex items-center justify-center">
                      <Drop size={20} weight="duotone" className="text-[#0ABAB5]" />
                    </div>
                    <div>
                      <p className="font-medium">{wash.program}</p>
                      <p className="text-white/50 text-sm font-mono">{wash.license_plate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">
                    <Check size={14} weight="bold" />
                    {t('completed')}
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <Calendar size={14} />
                    {formatDate(wash.completed_at)}
                  </div>
                  <span className="text-xs text-white/30 uppercase">
                    {wash.source === 'subscription' ? 'Subscription' : 'Single wash'}
                  </span>
                </div>
                
                {wash.extras?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {wash.extras.map((extra, i) => (
                      <span key={`${extra}-${i}`} className="px-2 py-0.5 text-xs bg-white/5 rounded-md text-white/50">
                        +{extra}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
